/**
 * Instances page — SuperAdmin CRUDL for ServisSearch Instances.
 * The SuperAdmin upstream key is stored in Secrets Manager (server-side) and
 * is never shown after entry. Only `hasSuperadminKey` is exposed to the client.
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { ConfirmDangerButton } from "@/components/common/ConfirmDangerButton";
import { LoadingRow, EmptyState, ErrorState } from "@/components/common/StateViews";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { instanceApi, type CreateInstanceInput } from "@/lib/api";
import type { InstancePublic } from "@/types";
import { ApiException } from "@/lib/api-client";

export function InstancesPage() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Omit<CreateInstanceInput, "color"> & { color: string }>({
    name: "", apiUrl: "", region: "eu-west-1", envLabel: "Dev", color: "#025d92", superadminKey: "",
  });
  const [formError, setFormError] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["instances"],
    queryFn: () => instanceApi.list(),
  });

  const createMut = useMutation({
    mutationFn: () =>
      instanceApi.create({
        name: form.name,
        apiUrl: form.apiUrl,
        region: form.region,
        envLabel: form.envLabel,
        color: form.color || null,
        superadminKey: form.superadminKey,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["instances"] });
      setShowCreate(false);
      setForm({ name: "", apiUrl: "", region: "eu-west-1", envLabel: "Dev", color: "#025d92", superadminKey: "" });
      setFormError(null);
    },
    onError: (err) => setFormError(err instanceof ApiException ? err.message : "Failed to create instance"),
  });

  const updateMut = useMutation({
    mutationFn: () =>
      instanceApi.update(editingId!, {
        apiUrl: form.apiUrl,
        region: form.region,
        envLabel: form.envLabel,
        color: form.color || null,
        superadminKey: form.superadminKey || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["instances"] });
      setShowCreate(false);
      setEditingId(null);
      setForm({ name: "", apiUrl: "", region: "eu-west-1", envLabel: "Dev", color: "#025d92", superadminKey: "" });
      setFormError(null);
    },
    onError: (err) => setFormError(err instanceof ApiException ? err.message : "Failed to update instance"),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => instanceApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["instances"] }),
  });

  const toggleMut = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      instanceApi.update(id, { isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["instances"] }),
  });

  const instances: InstancePublic[] = data?.instances ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Instances"
        description="Deployed ServisSearch APIs. Each holds a SuperAdmin key (stored in Secrets Manager, never shown)."
        actions={
          <Button onClick={() => { setEditingId(null); setShowCreate((v) => !v); }}>
            <Plus className="h-4 w-4" /> New instance
          </Button>
        }
      />

      {showCreate && (
        <form
          className="rounded-lg border bg-card p-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (editingId) updateMut.mutate();
            else createMut.mutate();
          }}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Name (typed at login)" id="i-name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} required disabled={Boolean(editingId)} />
            <Field label="API URL" id="i-url" value={form.apiUrl} onChange={(v) => setForm((f) => ({ ...f, apiUrl: v }))} placeholder="https://devapi.eu.servissearch.xyz" required />
            <Field label="Region" id="i-region" value={form.region} onChange={(v) => setForm((f) => ({ ...f, region: v }))} />
            <Field label="Env label" id="i-env" value={form.envLabel} onChange={(v) => setForm((f) => ({ ...f, envLabel: v }))} placeholder="Dev / Staging / Prod" />
            <div className="space-y-1.5">
              <Label htmlFor="i-color">Badge color</Label>
              <div className="flex items-center gap-2">
                <input id="i-color" type="color" className="h-9 w-12 rounded border bg-transparent" value={form.color}
                  onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))} />
                <Input value={form.color} onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="i-key">{editingId ? "Rotate SuperAdmin API key (optional)" : "SuperAdmin API key (stored encrypted, never re-shown)"}</Label>
              <Input id="i-key" type="password" value={form.superadminKey}
                onChange={(e) => setForm((f) => ({ ...f, superadminKey: e.target.value }))} required={!editingId} autoComplete="off" />
            </div>
          </div>
          {formError && <p className="text-sm text-destructive">{formError}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => { setShowCreate(false); setEditingId(null); }}>Cancel</Button>
            <Button type="submit" disabled={createMut.isPending || updateMut.isPending || !form.name || !form.apiUrl || (!editingId && !form.superadminKey)}>
              {editingId ? (updateMut.isPending ? "Saving…" : "Save instance") : (createMut.isPending ? "Creating…" : "Create instance")}
            </Button>
          </div>
        </form>
      )}

      {isLoading ? (
        <LoadingRow cols={4} />
      ) : error ? (
        <ErrorState message={error instanceof ApiException ? error.message : "Failed to load instances"} />
      ) : instances.length === 0 ? (
        <EmptyState message="No instances registered yet." />
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="w-36">Actions</TableHead>
                <TableHead>Region</TableHead>
                <TableHead>Env</TableHead>
                <TableHead>Key</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-16 text-right">Delete</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {instances.map((i) => (
                <TableRow key={i.instanceId}>
                  <TableCell>
                    <span className="flex items-center gap-2 font-medium">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: i.color ?? "var(--primary)" }} />
                      {i.name}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" title={i.isActive ? "Deactivate" : "Activate"}
                        disabled={toggleMut.isPending}
                        onClick={() => toggleMut.mutate({ id: i.instanceId, isActive: !i.isActive })}>
                        {i.isActive ? "Deactivate" : "Activate"}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Edit instance"
                        onClick={() => {
                          setEditingId(i.instanceId);
                          setShowCreate(true);
                          setForm({
                            name: i.name,
                            apiUrl: i.apiUrl,
                            region: i.region,
                            envLabel: i.envLabel,
                            color: i.color ?? "#025d92",
                            superadminKey: "",
                          });
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{i.region}</TableCell>
                  <TableCell className="text-muted-foreground">{i.envLabel}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={i.hasSuperadminKey ? "bg-green-50 text-green-700 border-green-200" : "bg-amber-50 text-amber-700 border-amber-200"}>
                      {i.hasSuperadminKey ? "configured" : "missing"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={i.isActive ? "bg-green-50 text-green-700 border-green-200" : "bg-muted text-muted-foreground"}>
                      {i.isActive ? "active" : "inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <ConfirmDangerButton
                      title="Delete instance"
                      resourceName={i.name}
                      actionLabel="Delete instance"
                      description="This removes the instance and its stored SuperAdmin key from this Admin UI."
                      disabled={deleteMut.isPending}
                      onConfirm={() => deleteMut.mutate(i.instanceId)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </ConfirmDangerButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function Field({ label, id, value, onChange, placeholder, required, disabled }: {
  label: string; id: string; value: string; onChange: (v: string) => void; placeholder?: string; required?: boolean; disabled?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required} disabled={disabled} />
    </div>
  );
}
